import {
  Center,
  Stack,
  Title,
  Text,
  Button,
  Paper,
  ThemeIcon,
  Group,
} from "@mantine/core";
import { IconLock } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth, useLogout } from "@/hooks/useAuth";

/**
 * Shown when a route refuses the user — either because their account carries no
 * role at all, or because it lacked a specific permission.
 *
 * F-051: this page used to state the FIRST reason unconditionally, so every
 * failed RouteGuard check told the user "no role has been assigned yet". It was
 * shown to an external auditor holding role:audit:external and an active
 * facility grant, mid-audit. The refusal was correct; the explanation was not.
 *
 * RouteGuard now forwards what it knew in navigation state, so the page can say
 * which of the two happened instead of guessing the more alarming one.
 */
interface NoAccessState {
  requiredPermissions?: string[];
  from?: string;
}

export function NoAccessPage() {
  const { data: context } = useAuth();
  const { state } = useLocation() as { state: NoAccessState | null };
  const { mutate: logout, isPending: isLoggingOut } = useLogout();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  /*
   * "No role" is a fact about the ACCOUNT; a failed permission check is a fact
   * about the ROUTE. Only the first justifies the Access Pending copy, and
   * conflating them is what made the message false for five personas.
   */
  const hasNoRole = (context?.roles?.length ?? 0) === 0;
  const required = state?.requiredPermissions ?? [];

  const handleCheckAgain = async () => {
    await queryClient.invalidateQueries({ queryKey: ["auth-context"] });
    navigate("/");
  };

  return (
    <Center h="100vh" bg="var(--mantine-color-gray-light)">
      <Paper withBorder shadow="md" p="xl" radius="md" maw={480} w="100%">
        <Stack align="center" gap="lg">
          <ThemeIcon size={64} radius="xl" color="orange" variant="light">
            <IconLock size={32} />
          </ThemeIcon>

          <Stack align="center" gap="xs">
            <Title order={1} fz="h2">
              {hasNoRole ? "Access Pending" : "Permission Required"}
            </Title>
            {hasNoRole ? (
              <Text c="dimmed" ta="center" size="sm">
                Your account has been created but no role has been assigned yet.
                Contact your administrator to gain access to LivingSyncs.
              </Text>
            ) : (
              <Text c="dimmed" ta="center" size="sm">
                Your account does not have permission to open this page. Contact
                your administrator if you need access.
              </Text>
            )}
            {!hasNoRole && required.length > 0 && (
              <Text c="dimmed" ta="center" size="xs">
                {state?.from ? `${state.from} requires ` : "Requires "}
                <strong>{required.join(", ")}</strong>
              </Text>
            )}
          </Stack>

          {context?.email && (
            <Text size="sm" c="dimmed">
              Signed in as <strong>{context.email}</strong>
            </Text>
          )}

          <Group>
            <Button variant="default" onClick={handleCheckAgain}>
              Check Again
            </Button>
            <Button
              color="red"
              variant="light"
              onClick={() => logout()}
              loading={isLoggingOut}
            >
              Sign Out
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Center>
  );
}
